import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Box, TextField } from "@material-ui/core";
import PropTypes from "prop-types";
import VoiceInputButton from "src/components/propuesta/VoiceInputButton";
import AICorrectButton from "src/components/propuesta/AICorrectButton";

const SYNC_DEBOUNCE_MS = 500;

/**
 * TextField del formulario v2 con state local + sincronización debounced
 * a Formik. Desacopla el tipeo del re-render global del form: cada keystroke
 * actualiza solo el state local; Formik se entera tras la pausa.
 *
 * Ediciones externas (botón IA, transcripción de voz, hidratación inicial)
 * llegan vía `formik.values[name]`; el efecto inferior copia ese valor al
 * state local cuando difiere — pero solo si NO es eco de un sync nuestro
 * (referencia `lastSyncedRef` evita loops).
 */
const CampoTextoIA = memo(function CampoTextoIA({
  formik,
  name,
  label,
  multiline,
  minRows,
  helperText,
  placeholder,
  contextIA,
  camposIA,
  desmarcarCampoIA,
  endActions,
}) {
  const formikValue = formik.values[name] ?? "";
  const [local, setLocal] = useState(formikValue);
  const lastSyncedRef = useRef(formikValue);
  const esIA = camposIA?.has(name) ?? false;

  // Edición externa: si el value de Formik cambió por algo que NO fuimos
  // nosotros (botón IA, hidratación), copiar al state local.
  useEffect(() => {
    if (formikValue !== lastSyncedRef.current && formikValue !== local) {
      setLocal(formikValue);
      lastSyncedRef.current = formikValue;
    }
  }, [formikValue, local]);

  // Sync local -> Formik con debounce. Solo dispara si el local diverge.
  useEffect(() => {
    if (local === formikValue) return undefined;
    const t = setTimeout(() => {
      lastSyncedRef.current = local;
      formik.setFieldValue(name, local);
    }, SYNC_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [local, formikValue, formik, name]);

  const handleChange = useCallback(
    (e) => {
      if (esIA) desmarcarCampoIA?.(name);
      setLocal(e.target.value);
    },
    [esIA, desmarcarCampoIA, name],
  );

  const handleBlur = useCallback(() => {
    // Flush inmediato al perder foco — evita perder la última pulsación
    // si el usuario sale antes del debounce.
    if (local !== formikValue) {
      lastSyncedRef.current = local;
      formik.setFieldValue(name, local);
    }
    formik.setFieldTouched(name, true);
  }, [local, formikValue, formik, name]);

  const handleVoiceTranscript = useCallback(
    (texto) => {
      const nuevo = local ? `${local} ${texto}` : texto;
      setLocal(nuevo);
    },
    [local],
  );

  const handleAICorrected = useCallback(
    (texto) => {
      setLocal(texto);
      if (esIA) desmarcarCampoIA?.(name);
    },
    [esIA, desmarcarCampoIA, name],
  );

  return (
    <Box>
      <TextField
        fullWidth
        multiline={multiline}
        minRows={minRows}
        label={label}
        name={name}
        value={local}
        onChange={handleChange}
        onBlur={handleBlur}
        helperText={helperText}
        placeholder={placeholder}
        sx={
          esIA
            ? { "& .MuiOutlinedInput-root": { backgroundColor: "#FFF3E0" } }
            : undefined
        }
      />
      <Box
        sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mt: 0.5 }}
      >
        <VoiceInputButton onTranscript={handleVoiceTranscript} />
        {contextIA ? (
          <AICorrectButton
            text={local}
            context={contextIA}
            onCorrected={handleAICorrected}
            disabled={!local}
          />
        ) : null}
        {endActions}
      </Box>
    </Box>
  );
});

CampoTextoIA.propTypes = {
  formik: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  multiline: PropTypes.bool,
  minRows: PropTypes.number,
  helperText: PropTypes.string,
  placeholder: PropTypes.string,
  contextIA: PropTypes.string,
  camposIA: PropTypes.instanceOf(Set),
  desmarcarCampoIA: PropTypes.func,
  endActions: PropTypes.node,
};

CampoTextoIA.defaultProps = {
  multiline: false,
  minRows: undefined,
  helperText: undefined,
  placeholder: undefined,
  contextIA: undefined,
  camposIA: undefined,
  desmarcarCampoIA: undefined,
  endActions: undefined,
};

export default CampoTextoIA;
